<script setup lang="ts">
import { getProductRoute } from "@shopware/helpers";

import { onClickOutside, useFocus, useMagicKeys } from "@vueuse/core";
import { useTemplateRef } from "vue";
import { useSearchStore } from "@/stores/search";
import { useNavigationTrailStore } from "@/stores/navigationTrail";

withDefaults(
  defineProps<{
    displayTotal?: number;
  }>(),
  { displayTotal: 10 },
);

defineEmits<(e: "link-clicked") => void>();

const { searchTerm, search, getProducts, getTotal, loading } =
  useProductSearchSuggest();

// True when the search bar is active and user can type in the search field
const active = ref(false);

// Reference to the search container
const searchContainer = useTemplateRef("searchContainer");
const searchInput = useTemplateRef("searchInput");
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const searchStore = useSearchStore();
const navigationTrailStore = useNavigationTrailStore();

const handleSuggestProductClick = () => {
  active.value = false;
  navigationTrailStore.markSearchNavigation();
};

watch(active, (value) => {
  const { focused } = useFocus(searchInput);

  focused.value = value;
});

// String the user has typed in the search field
const typingQuery = ref("");

watch(typingQuery, (value) => {
  if (value.length >= 3) {
    active.value = true;
    performSuggestSearch(value);
  }
});

// Defer the search request to prevent the search from being triggered too after typing
const performSuggestSearch = useDebounceFn((value) => {
  searchTerm.value = value;
  search();
}, 300);

// Suggest results will only be shown, when the search bar is active and the user has typed more than three characters in the search field
const showSuggest = computed(() => {
  return typingQuery.value.length >= 3 && active.value;
});

// Update search store when showSuggest changes
watch(showSuggest, (isVisible) => {
  searchStore.setSearchSuggestVisible(isVisible);
});

if (import.meta.client) {
  onClickOutside(searchContainer, () => {
    active.value = false;
  });
}

// @ts-expect-error - wait for vueuse to adapt to templateRefs
const { enter, escape } = useMagicKeys({ target: searchInput });
const { push } = useRouter();

const sideMenuModal = useSideMenuModal();

watch(enter, (value) => {
  if (!value) return;

  sideMenuModal.close();

  active.value = false;
  const searchKeyword = typingQuery.value;
  typingQuery.value = "";

  push(`/search?search=${searchKeyword}`);
});

// Handle escape key to close search results
watch(escape, (value) => {
  if (!value) return;

  active.value = false;
});

// Handle keydown events to show search results when typing continues
const handleKeydown = (event: KeyboardEvent) => {
  // Don't show results for escape key (handled by useMagicKeys)
  if (event.key === 'Escape') return;
  
  // If user is typing and has 3+ characters, show search results
  if (typingQuery.value.length >= 3) {
    active.value = true;
  }
};
</script>

<template>
  <div
    ref="searchContainer"
    class="relative search-bar opacity-100 visible lg:opacity-100 lg:visible transition-all duration-100 ease-in-out flex items-center w-full lg:w-[280px] xl:w-[360px] order-3 lg:order-2"
  >
    <div class="form-inner flex items-center w-full lg:border-b lg:border-grey-200">

      <input
        ref="searchInput"
        v-model="typingQuery"
        data-testid="layout-search-input"
        type="text"
        class="form-control bg-white w-full text-md border-none rounded-none focus:border-none h-[48px] px-4 -mx-4"
        :placeholder="$t('form.searchPlaceholder')"
        :aria-label="$t('form.ariaLabelSearch')"
        @click="active = true"
        @keydown="handleKeydown"
      />
      <button
        @click="performSuggestSearch(typingQuery)"
        class="text-lg search-button py-2 -mr-4 md:-mr-3 min-h-[48px] min-w-[48px] hover:text-primary focus:text-primary transition-colors duration-300 ease-in-out"
        :aria-label="$t('form.ariaLabelSearchSubmit')">
        <i class="icon-search-outline" aria-hidden="true"></i>
      </button>
    </div>
    <div
      data-testid="layout-search-result-box"
      class="hide-layout-search-result-box overflow-y-auto min-h-[60px] max-h-[calc(100dvh-140px)] absolute bg-white border-secondary-100 border-t-1 duration-300 top-12 left-0 mt-[2px] -mx-[20px] md:mx-0 py-3 overflow-hidden right-0 rounded-b-md shadow-md transition-height w-auto z-[60]"
    >
      <NuxtLink
        v-for="product in getProducts?.slice(0, displayTotal)"
        :key="product.id"
        :to="formatLink(getProductRoute(product))"
        data-testid="layout-search-suggest-link"
        @click="[handleSuggestProductClick(), $emit('link-clicked')]"
        class="block hover:bg-grey-50 hover:text-primary transition-colors duration-300 ease-in-out"
      >
        <ProductSuggestSearch :product="product" />
      </NuxtLink>

      <div
        class="h-auto text-sm rounded-b-md p-3 text-center transition mx-4"
      >
        <div
          v-if="loading"
          class="w-full py-4 flex items-center justify-center"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
          ></div>
        </div>
        <div v-else>
          <NuxtLink
            class="my-4 font-bold underline text-base hover:text-primary transition-colors duration-300 ease-in-out"
            v-if="getTotal > 0"
            data-testid="layout-search-result-box-more-link"
            :to="
              formatLink({ path: `/search`, query: { search: typingQuery } })
            "
            @click="[(active = false), $emit('link-clicked')]"
          >
            {{ $t("search.see") }}
            <span v-if="getTotal !== 1">{{ $t("search.all") }}</span>
            {{ getTotal }}
            <span>{{ $t("search.result", getTotal) }}</span>
          </NuxtLink>
          <div v-else data-testid="layout-search-result-box-no-result">
            {{ $t("search.noResults") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>